import React, { useState } from 'react'
import { Button } from './ui/button'
import { SearchInput } from './ui/search-input'
import { ListSkeleton } from './ui/skeleton-presets'
import { Plus, MessageSquare, MoreVertical, Archive, ArrowLeft } from '@/components/icons'
import { hapticLight } from '@/lib/haptics'
import { NewChatDrawer } from './NewChatDrawer'
import { useCurrentCrewMember } from '../hooks/useCurrentCrewMember'
import {
  useConversations,
  useSetConversationArchived,
  useArchivedUnreadChatCount,
} from '../lib/convex-hooks'
import { useCrewGetAll } from '../lib/hooks'
import { useNavigate, Link } from '@tanstack/react-router'
import dayjs from 'dayjs'
import { TopBarBackIconButton } from './topbar'
import { Card } from './ui/card'
import { AppLayout } from './AppLayout'
import { DesktopPage } from './desktop/DesktopPage'
import { DesktopPageHeader } from './desktop/DesktopPageHeader'
import { useIsDesktop } from './ui/use-mobile'
import { SwipeableRow } from './ui/swipeable-row'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog'
import { toast } from 'sonner'
import type { Id } from '@pronteer/convex/dataModel'

interface MessageThread {
  id: string
  type: 'dm' | 'group'
  name: string
  lastText: string
  time: string
  unreadCount?: number
}

interface ConversationsProps {
  archived?: boolean
}

export function Conversations({ archived = false }: ConversationsProps) {
  const navigate = useNavigate()
  const { data: currentUser } = useCurrentCrewMember()
  const [searchQuery, setSearchQuery] = useState('')
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [openRowId, setOpenRowId] = useState<string | null>(null)
  const [pendingId, setPendingId] = useState<Id<'conversations'> | null>(null)
  const [confirming, setConfirming] = useState(false)

  // Use Convex hooks for real-time data
  const convexConversations = useConversations(
    currentUser?.companyId,
    currentUser?.id,
    archived,
  )
  const { data: crewMembers = [], isLoading: crewLoading } =
    useCrewGetAll() as { data: any[]; isLoading: boolean }
  const setArchived = useSetConversationArchived()

  // Unread messages sitting in archived conversations → show a dot on the Archive menu item.
  const archivedUnreadCount = useArchivedUnreadChatCount(
    currentUser?.companyId,
    currentUser?.id,
  )
  const hasArchivedUnread = (archivedUnreadCount ?? 0) > 0

  // Everything the list (and its empty states) depends on: the skipped Convex
  // query while the user loads, the conversations themselves, and the crew
  // lookup — otherwise "No conversations"/"No crew" flashes before data lands.
  const loading =
    convexConversations === undefined || !currentUser || crewLoading

  // Transform Convex conversations into threads
  const threads = React.useMemo(() => {
    if (!convexConversations || !currentUser) return []

    // Convert Convex conversations to thread format
    const threadsData: MessageThread[] = (convexConversations as any[])
      .map((conv: any) => {
        // For DMs, check if it's a true self-conversation (no other participants)
        if (!conv.isGroup && (!conv.otherParticipants || conv.otherParticipants.length === 0)) {
          return null // Skip true self-conversations
        }

        // For DMs, derive name from other participants
        let displayName = conv.name || 'Unknown'
        if (!conv.isGroup && conv.otherParticipants && conv.otherParticipants.length > 0) {
          // Look up the other participant's name from crew members
          const otherParticipantCrewId = conv.otherParticipants[0].crewId
          const crewMember = crewMembers.find((cm: any) => cm.id === otherParticipantCrewId)

          if (crewMember) {
            // Use firstName + lastName if available, otherwise fall back to name
            displayName = crewMember.firstName && crewMember.lastName
              ? `${crewMember.firstName} ${crewMember.lastName}`
              : crewMember.name || 'Unknown'
          } else {
            // Fallback to conversation name if crew member not found
            displayName = conv.name || 'Unknown'
          }
        }

        return {
          id: conv._id,
          type: conv.isGroup ? ('group' as const) : ('dm' as const),
          name: displayName,
          lastText: conv.lastMessage || 'No messages yet',
          time: conv.lastMessageAt
            ? dayjs(conv.lastMessageAt).format('h:mm A')
            : 'Just now',
          unreadCount: conv.unreadCount || 0,
        }
      })
      .filter((t): t is Exclude<typeof t, null> => t !== null)

    return threadsData
  }, [convexConversations, currentUser, crewMembers])

  const otherCrewMembers = crewMembers.filter(
    (cm: any) => cm.id !== currentUser?.id
  )
  const hasNoCrew = !loading && otherCrewMembers.length === 0

  const filteredThreads = threads.filter((thread) => {
    const searchLower = searchQuery.toLowerCase()
    const name = (thread.name || '').toLowerCase()
    const lastText = (thread.lastText || '').toLowerCase()

    return name.includes(searchLower) || lastText.includes(searchLower)
  })

  const handleThreadClick = (threadId: string) => {
    navigate({ to: `/conversations/${threadId}` })
  }

  const handleSwipeAction = (threadId: string) => {
    setPendingId(threadId as Id<'conversations'>)
  }

  const handleConfirm = async () => {
    if (!pendingId || !currentUser) return
    setConfirming(true)
    try {
      await setArchived({
        conversationId: pendingId,
        crewId: currentUser.id,
        isArchived: !archived,
      })
      toast.success(archived ? 'Conversation unarchived' : 'Conversation archived')
      setPendingId(null)
      setOpenRowId(null)
    } catch {
      toast.error(archived ? 'Failed to unarchive' : 'Failed to archive')
    } finally {
      setConfirming(false)
    }
  }

  const dialogOpen = pendingId !== null

  const title = archived ? 'Archive' : 'Chat'
  const actionLabel = archived ? 'Unarchive' : 'Archive'

  const activeRightElement = (
    <div className="flex items-center gap-3">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            roundness="round"
            className="relative size-8 p-0 bg-content-primary-inverse hover:bg-content-primary-inverse/30 border-0 text-content-primary-bold"
          >
            <MoreVertical className="size-5" />
            {hasArchivedUnread && (
              <span className="absolute right-1 top-1 size-2 rounded-full bg-content-info-bold" />
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onSelect={() => navigate({ to: '/conversations/archive' })}
            className="flex items-center gap-2"
          >
            <Archive className="size-4 text-content-primary-subtle" />
            <span>Archive</span>
            {hasArchivedUnread && (
              <span className="ml-auto size-2 rounded-full bg-content-info-bold" />
            )}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <Button
        variant="brand"
        size="sm"
        roundness="round"
        onClick={() => {
          hapticLight()
          setDrawerOpen(true)
        }}
        className="size-8 p-0"
      >
        <Plus className="size-5 text-content-primary-white" />
      </Button>
    </div>
  )

  const isDesktop = useIsDesktop()

  const body = (
    <>
      {!archived && hasNoCrew ? (
        <div className="flex flex-col gap-[15px] pb-28">
          <SearchInput
            placeholder="Search messages"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <Card className="bg-surface-L2 p-2">
            <div className="flex flex-col items-center justify-center gap-4 py-6">
              <div className="flex items-center justify-center rounded-lg bg-background-brand-secondary-subtle p-2">
                <MessageSquare className="size-4 text-content-brand-bold" />
              </div>
              <div className="flex flex-col gap-1 text-center">
                <p className="text-base font-semibold text-content-primary-bold tracking-tight">
                  No crew to message
                </p>
                <p className="text-sm text-content-primary-subtle">
                  Add crew members before starting a conversation.
                </p>
              </div>
              <Button variant="primary" size="sm" onClick={() => {
                hapticLight()
                navigate({ to: '/operations/crew-directory', search: { openDrawer: true } })
              }} className="bg-background-brand-bold">
                <Plus className="size-3.5" />
                Add Crew
              </Button>
            </div>
          </Card>
        </div>
      ) : (
        <div className="flex flex-col gap-[15px] pb-28">
          {/* Search Input */}
          <SearchInput
            placeholder="Search messages"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />

          {/* Chat List Card */}
          <Card className={`p-2 ${threads.length === 0 && !loading ? 'bg-surface-L2' : ''}`}>
            {loading ? (
              <ListSkeleton />
            ) : threads.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-4 py-6">
                <div className="flex items-center justify-center rounded-lg bg-background-brand-secondary-subtle p-2">
                  <MessageSquare className="size-4 text-content-brand-bold" />
                </div>
                <div className="flex flex-col gap-1 text-center">
                  <p className="text-base font-semibold text-content-primary-bold tracking-tight">
                    {archived ? 'No archived conversations' : 'No conversations yet'}
                  </p>
                  <p className="text-sm text-content-primary-subtle">
                    {archived
                      ? 'Archived conversations will appear here.'
                      : 'Start a message to coordinate with your crew.'}
                  </p>
                </div>
                {!archived && (
                  <Button variant="primary" size="sm" onClick={() => {
                    hapticLight()
                    setDrawerOpen(true)
                  }} className="bg-background-brand-bold">
                    <Plus className="size-3.5" />
                    New Message
                  </Button>
                )}
              </div>
            ) : filteredThreads.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-center px-4">
                <p className="text-content-primary-subtle text-sm mb-4">
                  No messages found
                </p>
              </div>
            ) : (
              <div className="flex flex-col">
                {filteredThreads.map((thread) => {
                  const rowId = `conv-${thread.id}`
                  return (
                    <SwipeableRow
                      key={thread.id}
                      isOpen={openRowId === rowId}
                      onOpenChange={(o) => setOpenRowId(o ? rowId : null)}
                      onDelete={() => handleSwipeAction(thread.id)}
                      actionLabel={actionLabel}
                      actionClassName="bg-content-primary-subtle"
                      variant="icon"
                      actionIcon={<Archive />}
                    >
                      <ChatListItem
                        name={thread.name}
                        lastText={thread.lastText}
                        time={thread.time}
                        unreadCount={thread.unreadCount}
                        onClick={() => handleThreadClick(thread.id)}
                      />
                    </SwipeableRow>
                  )
                })}
              </div>
            )}
          </Card>

          {/* New Chat Drawer */}
          {!archived && (
            <NewChatDrawer open={drawerOpen} onOpenChange={setDrawerOpen} />
          )}
        </div>
      )}

      <AlertDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          if (!open && !confirming) {
            setPendingId(null)
          }
        }}
      >
        <AlertDialogContent className="bg-surface-L0">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {archived ? 'Unarchive Conversation' : 'Archive Conversation'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {archived
                ? 'This conversation will return to your main chat list.'
                : 'This conversation will be hidden from your main chat list. You can find it later in Archive.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={confirming}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirm} disabled={confirming}>
              {actionLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )

  const breadcrumb = archived ? (
    <span className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => navigate({ to: '/conversations' })}
        className="flex size-7 items-center justify-center rounded-full hover:bg-surface-L1"
        aria-label="Back to messages"
      >
        <ArrowLeft className="size-4" />
      </button>
      <span>
        <Link to="/conversations" className="hover:underline">
          Messages
        </Link>
        <span className="text-content-primary-subtle"> / Archive</span>
      </span>
    </span>
  ) : undefined

  if (isDesktop) {
    return (
      <DesktopPage>
        <DesktopPageHeader
          title={archived ? 'Archive' : 'Messages'}
          subtitle={archived ? undefined : 'Chat with your crew'}
          breadcrumb={breadcrumb}
          action={archived ? undefined : activeRightElement}
        />
        {body}
      </DesktopPage>
    )
  }

  return (
    <AppLayout
      topBar={
        <TopBarBackIconButton
          title={title}
          rightElement={archived ? undefined : activeRightElement}
          showBackButton={archived}
        />
      }
      className="pt-4"
    >
      {body}
    </AppLayout>
  )
}

interface ChatListItemProps {
  name: string
  lastText: string
  time: string
  unreadCount?: number
  onClick?: () => void
}

function ChatListItem({
  name,
  lastText,
  time,
  unreadCount = 0,
  onClick,
}: ChatListItemProps) {
  const hasUnread = unreadCount > 0

  return (
    <button
      onClick={onClick}
      className="flex items-start gap-3 w-full px-3 py-3.5 text-left hover:bg-background-brand-subtle active:bg-background-brand-subtle/80 transition-colors rounded-lg"
    >
      {/* Name, preview and time stacked */}
      <div className="flex-1 flex flex-col gap-1 min-w-0">
        <span className="text-base font-semibold text-content-primary-bold tracking-[-0.5px] leading-6">
          {name}
        </span>
        <p className="text-sm text-content-primary-subtle leading-5 line-clamp-2">
          {lastText}
        </p>
        <span className="text-xs font-semibold text-content-primary-subtle leading-4">
          {time}
        </span>
      </div>

      {/* Unread badge with alert dot */}
      {hasUnread && (
        <div className="shrink-0 pt-1.5">
          <span className="relative flex items-center justify-center h-[25px] min-w-[25px] px-[9px] py-[3px] rounded-lg bg-surface-L2 text-xs font-medium text-content-primary-bold leading-4">
            {unreadCount}
            <span className="absolute -right-0.5 -top-0.5 size-2.5 rounded-full bg-content-info-bold" />
          </span>
        </div>
      )}
    </button>
  )
}
